import { CommonForm } from '@/components/CommonForm';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonPhoneInput } from '@/components/CommonForm/CommonPhoneInput';
import { CommonServiceSelect } from '@/components/CommonForm/CommonServiceSelect';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';
import { useEnv } from '@/env/client';
import { useForm } from '@/hooks/useForm';
import { getDynamicsTopicConsents } from '@/tools/forms';
import { withSuspense } from '@/tools/withSuspense';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFieldMapping } from '@/types/dynamicsForms';
import { faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RichText } from '@lamesarv-sdk/components';
import { navigate, parsePhone } from '@lamesarv-sdk/tools';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { BaseOptInCheckbox, BaseSubmit, BaseSubscribeCheckbox } from '../base';
import { IFormProps, IFormResetterProps } from '../types';
import { IFormSchema, validationSchema } from './schema';

import * as FormClasses from '../classes';

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export const FormServiceAppointment = withSuspense((props: IFormProps) => {
  const { env } = useEnv();
  const { isLoading, hasError, submitted, sendForm } = useForm();

  const onSubmit = async (formInput: IFormSchema, metadata?: Record<string, unknown>) => {
    const mappings: IDynamicsFieldMapping[] = [
      { DataverseFieldName: 'lmrv_leadtype', DataverseFieldValue: String(DynamicsLeadType.service) },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource['website-online-inquiry']),
      },
      {
        DataverseFieldName: 'lmrv_service_center',
        DataverseFieldValue:
          String(props.servicesOptionSet.find((item) => item.Label === formInput.serviceCenter)?.DataverseValue) || '',
      },
      { DataverseFieldName: 'firstname', DataverseFieldValue: formInput.firstName },
      { DataverseFieldName: 'lastname', DataverseFieldValue: formInput.lastName },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.phone) },
      { DataverseFieldName: 'emailaddress1', DataverseFieldValue: formInput.email },
      { DataverseFieldName: 'lmrv_vehicleyear', DataverseFieldValue: formInput.year || '' },
      { DataverseFieldName: 'lmrv_make', DataverseFieldValue: formInput.make || '' },
      { DataverseFieldName: 'lmrv_model', DataverseFieldValue: formInput.model || '' },
      { DataverseFieldName: 'lmrv_vin', DataverseFieldValue: formInput.vin || '' },
      { DataverseFieldName: 'lmrv_comments', DataverseFieldValue: formInput.comments || '' },
      { DataverseFieldName: 'crbdb_segment', DataverseFieldValue: props.dynamics_segment || '' },
    ];

    if (formInput.subscribe || formInput.smsOptIn) {
      const topicChannels = [formInput.subscribe ? 'Email' : undefined, formInput.smsOptIn ? 'Text' : undefined]
        .filter(Boolean)
        .join(',');
      const topicConsents = getDynamicsTopicConsents({
        complianceProfileId: env.DYNAMICS_COMPLIANCE_PROFILE_ID || '',
        topicIDs: env.DYNAMICS_TOPIC_IDS || '',
        channels: topicChannels,
      });

      mappings.push(...topicConsents);
    }

    if (env.ENABLE_FORMS_CAPTCHA && env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE) {
      mappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: metadata?.captchaScore ? String(metadata?.captchaScore) : '',
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: metadata?.captchaThreshold ? String(metadata?.captchaThreshold) : '',
        },
      );
    }

    await sendForm({
      formKey: 'SERVICE',
      formName: props.custom_name || 'Form Service Appointment',
      mappings,
    });
  };

  const wasSuccessful = !isLoading && !hasError && submitted;

  return (
    <CommonForm
      id={props.custom_id || 'formServiceAppointment'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={isLoading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-service-appointment` : 'form-service-appointment'}
    >
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div className={twMerge('w-full mx-auto flex flex-col gap-4 p-1', props.classes_header)}>
          {props.title && <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>}
          {props.description && (
            <div className={twMerge('text-md-regular', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
        </div>
      )}
      <div className={twMerge('w-full mx-auto flex flex-col gap-4 p-1  bg-white', props.classes_container)}>
        {!props.extract_header && (
          <div className={twMerge('flex flex-col justify-center items-center gap-4', props.classes_header)}>
            {props.title && (
              <div className={twMerge('heading-h4 leading-none', props.classes_title)}>{props.title}</div>
            )}
            {props.description && (
              <div className={twMerge('text-md-regular', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
          </div>
        )}
        {isLoading && (
          <div
            className={
              'flex items-center w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faClock} className="w-8 mr-2" />
            <span>Sending...</span>
          </div>
        )}
        {wasSuccessful && (
          <div
            className={
              'flex items-center w-full bg-green-100 border border-green-500 text-green-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faCheckCircle} className="w-8 mr-2" />
            <span
              data-tag-group="form-success-label"
              data-tag={
                props.data_tag
                  ? `${props.data_tag}--form-service-appointment-success-label`
                  : 'form-service-appointment-success-label'
              }
            >
              Thank you for requesting more information.
            </span>
          </div>
        )}
        {!isLoading && hasError && (
          <div
            className={
              'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 mx-2 shadow-md'
            }
            role="alert"
          >
            <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
            <span>An error has occurred, please try again later or contact us using alternative methods.</span>
          </div>
        )}
        <div className={twMerge('flex flex-col', props.classes_content)}>
          <div className="flex flex-col">
            <h3 className="mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Personal Information</h3>
            <div className="my-2 px-2 w-full">
              <CommonServiceSelect
                name="serviceCenter"
                label="Preferred Service Center"
                classContainer="flex-1"
                servicesOptionSet={props.servicesOptionSet}
                isRequired
                hideLabel
                usePlaceholder
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput
                type="text"
                label="First Name"
                placeholder="First Name*"
                name="firstName"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput
                type="text"
                label="Last Name"
                placeholder="Last Name*"
                name="lastName"
                isRequired
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonPhoneInput label="Phone" placeholder="Phone*" name="phone" isRequired hideLabel />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="email" label="Email" placeholder="Email*" name="email" isRequired hideLabel />
            </div>
          </div>
          <div className="flex flex-col">
            <h3 className="my-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Coach/RV Information</h3>
            <div className="my-2 px-2 w-full">
              <CommonInput
                type="number"
                label="Year"
                placeholder="Year"
                name="year"
                customOptions={{ min: 0, step: 1 }}
                hideLabel
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="text" label="Make" placeholder="Make" name="make" hideLabel />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="text" label="Model" placeholder="Model" name="model" hideLabel />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonInput type="text" label="VIN" placeholder="VIN" name="vin" hideLabel />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonTextarea label="Comments" name="comments" placeholder="Comments" hideLabel />
            </div>
          </div>
          {props.display_subscribe_checkbox && (
            <div className="flex flex-row py-2 text-left">
              <BaseSubscribeCheckbox htmlContent={props.custom_subscribe_text} />
            </div>
          )}
          {props.display_sms_checkbox && (
            <div className="flex flex-row py-2">
              <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
            </div>
          )}
          <div className="flex flex-col">
            <div className="p-2">
              <BaseSubmit
                {...props}
                formNameSlug="form-service-appointment"
                styleClasses={FormClasses.buttonNormalClasses}
              />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
